import {jsx} from 'panel';

import {SelectTheme} from 'common/components/select/constants';
import {SelectEl} from 'common/components/select';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import {QueryBlockEl} from 'common/query-builder/query-block';
import {serializeAttributes} from 'common/util';
import {qbStyleAttrs} from 'common/query-builder/query-builder/util';
import {Section} from 'common/components/items-menu/types';
import {areSectionsEmpty} from 'common/components/items-menu/utils';
import {instrumentWithTimer} from 'common/util/workflow-timing';
import {getIconForPropertyType} from 'common/report/util/properties';
import {ButtonSize, ButtonTheme, ButtonType} from 'common/components/button/constants';
import {MultiAttributionMenuEl} from 'common/widgets/multi-attribution-menu';
import {MultiAttributionChangeEvent} from 'common/widgets/multi-attribution-menu/types';
import {DefaultMultiAttribution} from 'common/widgets/multi-attribution-menu/constants';
import {CountUsersOnceState, ResourceType} from 'common/report/constants';
import {
  generateCountCalculationMenuOptions,
  generateInsightsAdvancedCountMenuOptions,
  getSelectedCountBy,
} from 'common/query-builder/query-block/count-menu/util';
import {ButtonEl} from 'common/components/button';
import {SectionItemProperty} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import {MetricType} from 'common/query-builder/query-block/constants';

import type {QueryEntrySection} from '../index';
import {
  CountBy,
  Entry,
  isFormulaEntry,
  isMetricEntry,
  isUserBasedEntry,
  QueryMetricEntry,
  SelectorTypeAggregation,
} from '../types';
import {getSelectedCalculation, getSelectedCalculationLabel} from '../util';

/**
 * Defines the 1st pass aggregation function
 */
export function RowNumericAggregator({el, entry, index}: {el: QueryEntrySection; entry: Entry; index: number}) {
  const hasNewUI = el.hasNewQBUI();
  const sections = el.getSelectorAggregationsForEntry();
  if (!sections.length) {
    return null;
  }

  return (
    <div sel='.mp-query-entry-aggregator'>
      <SelectEl
        key='entry-aggregator'
        attrs={{
          elref: `aggregatorSelect`,
          'always-show-caret': true,
          'selected-option': el.getSelectedAggregationData({entry: entry as QueryMetricEntry})
            .firstPassAggregationLabel,
          'disable-search': true,
          'menu-width': null,
          'menu-height': null,
          theme: hasNewUI ? SelectTheme.Tertiary : SelectTheme.Select,
          icon: hasNewUI && {rightIcon: `double-caret`},
          'track-name': `operator`,
        }}
        on={{
          change: instrumentWithTimer((ev: CustomEvent) =>
            el.onEntryAggregationFunctionSelected({index, aggregation: ev.detail.item.value, isSubAggregation: false}),
          ),
          menuChange: (ev) => el.helpers.onSelectMenuChange(ev, index, el.helpers.EntryPart.Aggregator),
        }}
        props={{
          sections,
        }}
      />
    </div>
  );
}

/**
 * Defines the 2nd pass aggregation for a given metric
 *
 * For instance, if working on a per-user level aggregation (event count, property aggregation), aa 2-nd level
 * aggregation can be "from all of the distinct counts per-user, aggregate all on a distribution" distribution
 * in this example is the 2nd pass aggregation
 */
export function RowNumericSubAggregator({el, entry, index}: {el: QueryEntrySection; entry: Entry; index: number}) {
  const hasNewUI = el.hasNewQBUI();
  const generateSectionsFromAggregations = (aggregations: SelectorTypeAggregation[]): Section[] => {
    if (!aggregations) {
      return [];
    }

    const items = aggregations.map((aggregation) => el.generateAggregationSectionItem(aggregation));
    return [{items}];
  };

  const aggregatorEl = (trackName: string, selectedOption: string, sections: Section[], label = null) =>
    !areSectionsEmpty(sections) && (
      <>
        {!hasNewUI && `Aggregate by`}
        <SelectEl
          key='entry-subaggregator'
          attrs={{
            elref: `subAggregatorSelect`,
            ...(label ? {label} : null),
            'selected-option': selectedOption,
            'disable-search': true,
            'menu-width': null,
            'menu-height': 340,
            'track-name': trackName,
            theme: hasNewUI ? SelectTheme.Tertiary : SelectTheme.Select,
            tooltip: {placement: hasNewUI ? Placement.Bottom : Placement.Top},
          }}
          on={{
            change: (ev: CustomEvent) =>
              el.onEntryAggregationFunctionSelected({
                index,
                aggregation: ev.detail.item.value,
                isSubAggregation: true,
              }),
            menuChange: (ev) => el.helpers.onSelectMenuChange(ev, index, el.helpers.EntryPart.SubAggregator),
          }}
          props={{sections}}
        />
      </>
    );

  const {firstPassAggregation, firstPassAggregationLabel, secondPassAggregationLabel} = el.getSelectedAggregationData({
    entry: entry as QueryMetricEntry,
    forNewWorkflow: true,
  });
  const firstPassAggregations = el.getSelectorAggregationsForEntry();
  const secondPassAggregations = generateSectionsFromAggregations(firstPassAggregation.subAggregations);

  if (!firstPassAggregations.length && !secondPassAggregations.length) {
    return null;
  }

  return (
    <div sel='.mp-query-entry-subaggregator'>
      {/* 1st-pass operator (Distinct Count per user) */}
      {hasNewUI && aggregatorEl(`1-pass`, firstPassAggregationLabel, firstPassAggregations)}
      {/* 2-pass operator (by Distribution) */}
      {aggregatorEl(
        `2-pass`,
        secondPassAggregationLabel,
        secondPassAggregations,
        hasNewUI && `by ${secondPassAggregationLabel}`,
      )}
    </div>
  );
}

export function CountBySelection({el, entry, index}: {el: QueryEntrySection; entry: Entry; index: number}) {
  const {dataGroups, openEntry} = el.state;
  const {index: activeIndex, part: activePart} = openEntry;
  const dateUnit = el.attr(`date-unit`);
  const plotStyle = el.attr(`plot-style`);
  const isUserBased = isUserBasedEntry(entry);
  const dataGroupId = isMetricEntry(entry) ? entry.behavior.dataGroupId : entry.dataGroupId;

  const selectedCalculation = getSelectedCalculation(entry as QueryMetricEntry);
  const selectedCalculationLabel = getSelectedCalculationLabel(selectedCalculation, dateUnit);
  const advancedMenuOptions = isUserBased
    ? []
    : generateInsightsAdvancedCountMenuOptions({
        selectedPlotStyle: plotStyle,
        selectedCalculation,
        selectedDateUnit: dateUnit,
      });

  const calculationSelectorOptions = isUserBased
    ? []
    : generateCountCalculationMenuOptions({
        selectedPlotStyle: plotStyle,
        selectedCalculation,
        selectedDateUnit: dateUnit,
      });

  const queryMetricEntry = entry as QueryMetricEntry;
  return (
    <>
      <QueryBlockEl
        sel='.subitem-style'
        attrs={serializeAttributes({
          elref: `countBlock`,
          type: `count`,
          'metric-type': isUserBased ? MetricType.People : MetricType.Event,
          open: activeIndex === index && activePart === el.helpers.EntryPart.SubAggregator,
          'advanced-menu-sections': advancedMenuOptions,
          'all-data-groups': el.attr(`data-groups-enabled`),
          'count-users-once-state': el.getMultiAttribution(index)
            ? CountUsersOnceState.Required
            : el.attr(`count-users-once-state`),
          'chart-type': el.attr(`chart-type`),
          'data-group-id': el.attr(`data-group-id`) || dataGroupId,
          'data-groups': dataGroups,
          'date-range': JSON.parse(el.attr(`date-range`)),
          'date-unit': el.attr(`date-unit`),
          'enable-workspace-filter-restrictions': el.attr(`enable-workspace-filter-restrictions`),
          'event-names':
            !isFormulaEntry(queryMetricEntry) && queryMetricEntry.event?.value ? [queryMetricEntry.event.value] : [],
          'include-custom-properties': el.attr(`include-custom-properties`),
          'hide-events': !el.helpers.isEventBasedEntry(entry),
          'other-aggregation-options': el.attr(`other-aggregation-options`),
          'hide-edit-controls': el.attr(`hide-edit-controls`),
          'project-has-people': el.attr(`project-has-people`) && isUserBased,
          'project-id': el.attr(`project-id`),
          readonly: entry.type === ResourceType.Cohort,
          'scd-poc-enabled': el.attr(`scd-poc-enabled`),
          'selector-type': `button`,
          selected: getSelectedCountBy(queryMetricEntry),
          'highlight-selections': true,
          theme: SelectTheme.QueryBuilder,
          'button-size': ButtonSize.Small,
          'track-name': el.attr(`track-name`),
          'user-id': el.attr(`user-id`),
          'workspace-id': el.attr(`workspace-id`),
        })}
        on={{
          select: ({detail: {item}}: CustomEvent<{item: CountBy}>) => el.handleCountBySelected(index, item),
          open: () => el.helpers.setActiveEntry(`open`, {index, part: el.helpers.EntryPart.SubAggregator}),
          close: () => el.helpers.setActiveEntry(`close`, {index, part: el.helpers.EntryPart.SubAggregator}),
          track: el.helpers.preventTracking,
        }}
      />
      {selectedCalculation.calculationType && (
        <div sel='.calculation-selector'>
          <SelectEl
            key='calculation-selector'
            attrs={{
              elref: `calculationSelector`,
              label: selectedCalculationLabel,
              'disable-search': true,
              'menu-width': 180,
              'track-name': el.attr(`track-name`),
              theme: SelectTheme.Tertiary,
              'disable-tooltip': true,
            }}
            on={{
              change: (ev: CustomEvent) => el.handleCalculationSelected(index, ev.detail.item.value),
              menuChange: (ev) => el.helpers.onSelectMenuChange(ev, index, el.helpers.EntryPart.Calculation),
            }}
            props={{sections: calculationSelectorOptions}}
          />
          <ButtonEl
            sel='.remove-button'
            attrs={{
              theme: ButtonTheme.Tertiary,
              size: ButtonSize.Inline,
              type: ButtonType.Destructive,
              tooltip: {
                placement: Placement.RightCenter,
                size: DisplaySize.Small,
                text: `Remove`,
              },
              icon: `x`,
            }}
            on={{
              click: () => el.handleCalculationRemoved(index),
            }}
          />
        </div>
      )}
    </>
  );
}

/**
 * Defines the property a given aggregation metric is working on
 */
export function RowNumericProperty({
  el,
  entry,
  index,
  isPlaceholder,
  handleSelect,
}: {
  el: QueryEntrySection;
  entry: QueryMetricEntry;
  index: number;
  isPlaceholder: boolean;
  handleSelect: (property) => void;
}) {
  const {index: activeIndex, part: activePart} = el.state.openEntry;
  const isUserBased = isUserBasedEntry(entry);
  const property = entry.property?.value ? entry.property : null;

  const type = (property as SectionItemProperty)?.joinPropertyType ?? property?.type ?? null;
  const icon = property ? getIconForPropertyType(type, {isCustom: `customProperty` in property}) : `type-number`;

  return (
    <QueryBlockEl
      key='event-property'
      class={{
        'is-placeholder': isPlaceholder,
      }}
      attrs={serializeAttributes({
        elref: `propertyAggregationBlock`,
        type: `properties`,
        open: isPlaceholder && activeIndex === index && activePart === el.helpers.EntryPart.NumericProperty,
        'all-data-groups': el.attr(`data-groups-enabled`),
        'data-group-id': el.attr(`data-group-id`) || entry.dataGroupId,
        'enable-workspace-filter-restrictions': el.attr(`enable-workspace-filter-restrictions`),
        'event-names': !isFormulaEntry(entry) && entry.event?.value ? [entry.event.value] : [],
        'include-custom-properties': el.attr(`include-custom-properties`),
        'hide-events': !el.helpers.isEventBasedEntry(entry),
        'hide-edit-controls': el.attr(`hide-edit-controls`),
        'show-special-date-property-unit': true,
        'project-has-people': el.attr(`project-has-people`) && isUserBased,
        'project-id': el.attr(`project-id`),
        selected: property,
        'selector-type': `button`,
        'selector-icon': icon ?? `type-number`,
        theme: qbStyleAttrs(el.hasNewQBUI()).selectorTheme,
        'track-name': el.attr(`track-name`),
        'user-id': el.attr(`user-id`),
        'workspace-id': el.attr(`workspace-id`),
      })}
      props={{contextPanelParentElement: el.helpers.getContextPanelParentEl(index)}}
      on={{
        select: ({detail: {item}}: CustomEvent) => handleSelect(item),
        remove: () => handleSelect(null),
        open: () => el.helpers.setActiveEntry(`open`, {index, part: el.helpers.EntryPart.NumericProperty}),
        close: () => el.helpers.setActiveEntry(`close`, {index, part: el.helpers.EntryPart.NumericProperty}),
        track: el.helpers.preventTracking,
      }}
    />
  );
}

export function MultiAttributionAggregator({el, index}: {el: QueryEntrySection; index: number}) {
  const hasNewUI = el.hasNewQBUI();
  const isOpen = el.isMultiAttributionMenuOpen(index);
  const multiAttribution = el.getMultiAttribution(index);
  if (!(hasNewUI && (isOpen || multiAttribution))) {
    return null;
  }

  return (
    <div sel='.subitem-text.multi-attribution-type'>
      <SelectEl
        sel='.subitem-select.multi-attribution-type'
        attrs={{
          elRef: `multiAttributionSelector`,
          label: el.getMultiAttributionLabel(index),
          title: isOpen ? `` : el.getMultiAttributionTitle(index),
          open: isOpen,
          theme: SelectTheme.QbSubitem,
          size: ButtonSize.Small,
        }}
        on={{
          menuChange: (ev) => el.onMultiAttributionMenuChange(ev, index),
        }}
      >
        <div attrs={{slot: `body`}}>
          {isOpen && (
            <MultiAttributionMenuEl
              params={{
                handleCancel: () => {
                  el.onMultiAttributionCancel();
                },
                handleChange: (ev: MultiAttributionChangeEvent) => {
                  el.onMultiAttributionChange(ev, index);
                },
                handleDelete: () => {
                  el.onMultiAttributionDelete(index);
                },
                isSupportedMultiAttributionType: (type) => el.isSupportedMultiAttributionTypeForEntry(type, index),
                metricIndex: index,
                multiAttribution: multiAttribution || DefaultMultiAttribution,
                projectIsFreePlan: el.attr(`project-is-free-plan`),
              }}
            />
          )}
        </div>
      </SelectEl>
    </div>
  );
}
